import React, { useState } from 'react';
import { projectsAPI } from '../../services/api';

const VersionHistory = ({ versions, projectId }) => {
  const [selectedVersion, setSelectedVersion] = useState(null);
  const [versionFiles, setVersionFiles] = useState(null);
  const [loading, setLoading] = useState(false);
  const [downloadLoading, setDownloadLoading] = useState(null);
  const [error, setError] = useState(null);

  // 🔥 FIX: Safe default for versions
  const safeVersions = versions || [];

  const loadVersionFiles = async (version) => {
    try {
      setLoading(true);
      setError(null);
      setSelectedVersion(version);

      console.log('Loading files for version:', version.id, 'project:', projectId);

      const versionData = await projectsAPI.getVersion(projectId, version.id);
      console.log('Version data received:', versionData);

      setVersionFiles(versionData.files || []);
    } catch (error) {
      console.error('Error loading version files:', error);
      setError('Failed to load version files: ' + (error.message || 'Unknown error'));
      setVersionFiles([]);
    } finally {
      setLoading(false);
    }
  };

  const closeVersionFiles = () => {
    setSelectedVersion(null);
    setVersionFiles(null);
    setError(null);
  };

  const handleDownloadVersion = async (version) => {
    try {
      setDownloadLoading(version.id);
      setError(null);

      // Create a downloadable file from version files
      const versionData = await projectsAPI.getVersion(projectId, version.id);
      const files = versionData.files || [];

      if (files.length === 0) {
        throw new Error('No files available for download');
      }

      // Create a structured text file with all files
      const fileContents = files.map(file =>
        `=== FILE: ${file.name} ===\n${file.content}\n${'='.repeat(50 + file.name.length)}\n\n`
      ).join('\n');

      // Create and download the file
      const blob = new Blob([fileContents], { type: 'text/plain' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `version_v${version.version_number}_${new Date(version.generated_at).toISOString().split('T')[0]}.txt`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

    } catch (error) {
      console.error('Download error:', error);
      setError('Failed to download version: ' + (error.message || 'Unknown error'));
    } finally {
      setDownloadLoading(null);
    }
  };

  const getModelBadgeColor = (modelName) => {
    if (!modelName) return 'bg-gray-100 text-gray-800 border border-gray-300';

    const modelColors = {
      'gemini': 'bg-blue-100 text-blue-800 border border-blue-300',
      'claude': 'bg-purple-100 text-purple-800 border border-purple-300',
      'llama': 'bg-orange-100 text-orange-800 border border-orange-300',
      'mistral': 'bg-green-100 text-green-800 border border-green-300',
      'gpt': 'bg-emerald-100 text-emerald-800 border border-emerald-300'
    };

    for (const [key, color] of Object.entries(modelColors)) {
      if (modelName.toLowerCase().includes(key)) {
        return color;
      }
    }

    return 'bg-gray-100 text-gray-800 border border-gray-300';
  };

  const truncatePrompt = (prompt, maxLength = 120) => {
    if (!prompt) return 'No prompt available';
    if (prompt.length <= maxLength) return prompt;
    return prompt.substring(0, maxLength) + '...';
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // File viewer functions (copied from your FileViewer for consistency)
  const getFileIcon = (filename) => {
    const extension = filename.split('.').pop()?.toLowerCase();
    const icons = {
      js: '📄', ts: '📄', jsx: '📄', tsx: '📄',
      py: '🐍', python: '🐍',
      html: '🌐', htm: '🌐',
      css: '🎨', scss: '🎨', sass: '🎨',
      json: '📋',
      md: '📝', markdown: '📝',
      txt: '📄',
      xml: '📄',
      yml: '⚙️', yaml: '⚙️',
      java: '☕',
      c: '🔧', cpp: '🔧', h: '🔧',
      php: '🐘',
      rb: '💎',
      go: '🔷',
      rs: '🦀',
      sql: '🗃️',
      sh: '💻', bash: '💻',
      dockerfile: '🐳',
      gitignore: '🔒',
      env: '⚙️'
    };
    return icons[extension] || '📄';
  };

  const getLanguage = (filename) => {
    const extension = filename.split('.').pop()?.toLowerCase();
    const languages = {
      js: 'javascript', ts: 'typescript', jsx: 'javascript', tsx: 'typescript',
      py: 'python', python: 'python',
      html: 'html', htm: 'html',
      css: 'css', scss: 'scss', sass: 'sass',
      json: 'json',
      md: 'markdown', markdown: 'markdown',
      txt: 'text',
      xml: 'xml',
      yml: 'yaml', yaml: 'yaml',
      java: 'java',
      c: 'c', cpp: 'cpp', h: 'c',
      php: 'php',
      rb: 'ruby',
      go: 'go',
      rs: 'rust',
      sql: 'sql',
      sh: 'bash', bash: 'bash'
    };
    return languages[extension] || 'text';
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-xl font-bold text-gray-900">Version History</h3>
          <p className="text-sm text-gray-600 mt-1">
            {safeVersions.length} version{safeVersions.length !== 1 ? 's' : ''} generated
          </p>
        </div>
        <div className="flex items-center space-x-2 text-sm text-gray-500">
          <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
          <span>AI Powered</span>
        </div>
      </div>

      {/* Version Timeline */}
      <div className="space-y-4">
        {safeVersions.length === 0 ? (
          <div className="text-center py-12 bg-gradient-to-br from-gray-50 to-blue-50 rounded-2xl border border-gray-200">
            <div className="text-6xl mb-4">🚀</div>
            <h4 className="text-lg font-semibold text-gray-900 mb-2">No versions yet</h4>
            <p className="text-gray-600">Generate your first version to see the history here</p>
          </div>
        ) : (
          <div className="relative space-y-6">
            {safeVersions.map((version, index) => (
              <div key={version.id} className="relative">
                {/* Timeline connector */}
                {index < safeVersions.length - 1 && (
                  <div className="absolute left-6 top-14 bottom-0 w-0.5 bg-gradient-to-b from-blue-300 to-transparent"></div>
                )}

                {/* Version card */}
                <div className="bg-white rounded-xl border border-gray-200 hover:border-blue-300 transition-all duration-200 hover:shadow-lg">
                  <div className="p-6">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-3">
                          {/* Version number with gradient */}
                          <div className="flex items-center justify-center w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl text-white font-bold text-lg">
                            v{version.version_number}
                          </div>

                          <div className="flex-1">
                            <div className="flex items-center space-x-2 mb-1">
                              <span className="text-sm font-medium text-gray-900">
                                {formatDate(version.generated_at)}
                              </span>
                              {version.ai_model && (
                                <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getModelBadgeColor(version.ai_model)}`}>
                                  {version.ai_model.split('/').pop()}
                                </span>
                              )}
                            </div>

                            {/* Prompt preview */}
                            <p className="text-sm text-gray-700 leading-relaxed">
                              {truncatePrompt(version.prompt_used)}
                            </p>
                          </div>
                        </div>
                      </div>

                      <div className="flex flex-col space-y-2 ml-4">
                        <button
                          onClick={() => loadVersionFiles(version)}
                          disabled={loading && selectedVersion?.id === version.id}
                          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-lg text-white bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 transition-all duration-200"
                        >
                          {loading && selectedVersion?.id === version.id ? (
                            <span className="flex items-center">
                              <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                              </svg>
                              Loading...
                            </span>
                          ) : (
                            <span className="flex items-center">
                              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                              </svg>
                              View Files
                            </span>
                          )}
                        </button>

                        {/* 🔥 NEW: Download this version button */}
                        <button
                          onClick={() => handleDownloadVersion(version)}
                          disabled={downloadLoading === version.id}
                          className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 transition-all duration-200"
                        >
                          {downloadLoading === version.id ? (
                            <span className="flex items-center">
                              <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-gray-500" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                              </svg>
                              Downloading...
                            </span>
                          ) : (
                            <span className="flex items-center">
                              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                              </svg>
                              Download this version
                            </span>
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Version Files Modal */}
      {selectedVersion && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl shadow-2xl max-w-6xl w-full max-h-[90vh] overflow-hidden border border-gray-300">
            <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-blue-50">
              <div>
                <h3 className="text-xl font-bold text-gray-900 flex items-center space-x-2">
                  <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg flex items-center justify-center text-white font-bold">
                    v{selectedVersion.version_number}
                  </div>
                  <span>Version Files</span>
                </h3>
                <p className="text-sm text-gray-600 mt-1">
                  Generated: {formatDate(selectedVersion.generated_at)}
                  {selectedVersion.ai_model && (
                    <span className="ml-2">• Model: {selectedVersion.ai_model}</span>
                  )}
                </p>
                <p className="text-sm text-gray-700 mt-2 max-w-2xl">
                  {selectedVersion.prompt_used}
                </p>
              </div>
              <div className="flex items-center space-x-2">
                {/* Download button in modal */}
                <button
                  onClick={() => handleDownloadVersion(selectedVersion)}
                  disabled={downloadLoading === selectedVersion.id}
                  className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 transition-all duration-200"
                >
                  {downloadLoading === selectedVersion.id ? (
                    <span className="flex items-center">
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-gray-500" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Downloading...
                    </span>
                  ) : (
                    <span className="flex items-center">
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      Download
                    </span>
                  )}
                </button>
                <button
                  onClick={closeVersionFiles}
                  className="text-gray-400 hover:text-gray-600 text-xl bg-white rounded-full p-2 hover:bg-gray-100 transition-colors"
                >
                  ✕
                </button>
              </div>
            </div>

            <div className="p-6 overflow-y-auto max-h-[70vh]">
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4">
                  {error}
                </div>
              )}

              {loading ? (
                <div className="text-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                  <p className="text-gray-600 font-medium">Loading version files...</p>
                </div>
              ) : versionFiles && versionFiles.length > 0 ? (
                <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
                  {/* File Navigation */}
                  <div className="lg:col-span-1">
                    <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                      <h4 className="text-lg font-semibold text-gray-900 mb-4">Files</h4>
                      <div className="space-y-2 max-h-96 overflow-y-auto">
                        {versionFiles.map((file, index) => (
                          <button
                            key={index}
                            onClick={() => setSelectedVersion({ ...selectedVersion, currentFile: file })}
                            className={`w-full text-left px-3 py-2 rounded-lg text-sm flex items-center space-x-2 transition-all ${
                              selectedVersion.currentFile?.name === file.name
                                ? 'bg-gradient-to-r from-blue-500 to-purple-600 text-white shadow-lg'
                                : 'text-gray-700 hover:bg-white hover:shadow-md border border-transparent hover:border-gray-200'
                            }`}
                          >
                            <span className="text-base">{getFileIcon(file.name)}</span>
                            <span className="truncate flex-1">{file.name}</span>
                            <span className="text-xs opacity-70">
                              {getLanguage(file.name)}
                            </span>
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* File Content */}
                  <div className="lg:col-span-3">
                    {selectedVersion.currentFile ? (
                      <div className="bg-white rounded-xl border border-gray-200 h-full">
                        <div className="bg-gray-50 px-4 py-3 border-b border-gray-200 flex items-center justify-between rounded-t-xl">
                          <div className="flex items-center space-x-2">
                            <span className="text-lg">{getFileIcon(selectedVersion.currentFile.name)}</span>
                            <h4 className="text-sm font-medium text-gray-900">
                              {selectedVersion.currentFile.name}
                            </h4>
                            <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-gray-200 text-gray-800">
                              {getLanguage(selectedVersion.currentFile.name)}
                            </span>
                          </div>
                          <div className="text-xs text-gray-500">
                            {selectedVersion.currentFile.content?.length || 0} characters
                          </div>
                        </div>
                        <div className="p-4 max-h-96 overflow-y-auto bg-gray-50 rounded-b-xl">
                          <pre className="text-sm text-gray-800 whitespace-pre-wrap font-mono bg-white p-4 rounded-lg border border-gray-200">
                            {selectedVersion.currentFile.content}
                          </pre>
                        </div>
                      </div>
                    ) : (
                      <div className="text-center py-12 bg-gradient-to-br from-gray-50 to-blue-50 rounded-2xl border border-gray-200">
                        <div className="text-4xl mb-4">📄</div>
                        <h4 className="text-lg font-semibold text-gray-900 mb-2">Select a file</h4>
                        <p className="text-gray-600">Choose a file from the sidebar to view its content</p>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div className="text-center py-12 bg-gradient-to-br from-gray-50 to-blue-50 rounded-2xl border border-gray-200">
                  <div className="text-4xl mb-4">📝</div>
                  <h4 className="text-lg font-semibold text-gray-900 mb-2">No files available</h4>
                  <p className="text-gray-600">This version doesn't contain any files</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VersionHistory;